import type {
	CacheConfig,
	RequestParameters,
	Variables,
	GraphQLSingularResponse,
	PayloadError,
} from 'relay-runtime';
import {
	captureErrors,
	isUnauthenticated,
} from '@jira/platform__graphql-relay-errors/src/index.tsx';
import { recordLatestErrors } from '@jira/platform__graphql-relay-errors/src/getLatestErrors.tsx';
import {
	getQueryResponseCache,
	type SSRQueryResponseCache,
} from '@jira/platform__graphql-relay-query-responses/src/index.tsx';
import {
	claimEarlyScriptPromise,
	getEarlyScriptPromiseKey,
} from '@jira/platform__graphql-relay-vendorless-promise/src/index.tsx';

import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import { getAnalyticsWebClientPromise } from '@jira/platform__observability-product-analytics-web-client-async';
import { fg } from '@atlassian/jira-feature-gating';
import { getReroutableURL } from '@atlassian/jira-fetch/src/utils/get-reroutable-url.tsx';
import { withErrorHandling } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import HttpError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { showUnauthenticatedFlag } from './services/error-flags/index.tsx';
import { getLoggingAndMetricsHelper } from './services/logging-and-metrics/index.tsx';
import { blocklist, checkAuthentication, generateOptions } from './utils.tsx';

function fireUnauthenticatedResponseAnalytics({
	request,
	response,
}: {
	request: RequestParameters;
	response: GraphQLSingularResponse;
}): void {
	if (!fg('relay_fetch_unauthenticated_response_analytics')) {
		return;
	}

	getAnalyticsWebClientPromise().then((client) => {
		const analyticsClient = client.getInstance();
		if (!analyticsClient) {
			return;
		}

		analyticsClient.sendOperationalEvent({
			action: 'detected',
			actionSubject: 'unauthenticatedGraphqlResponse',
			attributes: {
				operation: request.name,
				operationKind: request.operationKind,
				hasData: Boolean(response.data),
			},
			source: 'jiraRelayFetch',
		});
	});
}

function validateResponse(request: RequestParameters, response: GraphQLSingularResponse): boolean {
	const errors = 'errors' in response ? response.errors : undefined;
	if (errors != null) {
		const requestId = response?.extensions?.gateway?.request_id;
		if (!blocklist.includes(request.name)) {
			getLoggingAndMetricsHelper({
				queryName: request.name,
				operationKind: request.operationKind,
			}).logNonBreakingErrors(errors, requestId);
		}
		// @ts-expect-error -- AGGError.path has a type of `string[]`, whereas PayloadError.path has a type of `(string | number)[]`

		captureErrors(errors, { operationName: request.name, requestId });

		// @ts-expect-error -- AGGError.path has a type of `string[]`, whereas PayloadError.path has a type of `(string | number)[]`
		if (isUnauthenticated(errors)) {
			fireUnauthenticatedResponseAnalytics({
				request,
				response,
			});
			return false;
		}
	}
	return true;
}

function onInvalidResponse(request: RequestParameters, _response: GraphQLSingularResponse): void {
	//  Short circuit operation and handle the unauthorized errors.
	showUnauthenticatedFlag(request.name);
}

// Records a response's AGG errors + gateway ids for the operation. relay-fetch calls this on the
// client SSR-cache-read path, which bypasses validateResponse. Whether to actually capture is a
// product concern, so the feature gate lives here rather than in the platform relay-fetch code.
function captureResponseErrors(
	request: RequestParameters,
	response: GraphQLSingularResponse,
): void {
	if (!fg('fun-2360_relay_rate_limit_monitor')) {
		return;
	}

	const errors = 'errors' in response ? response.errors : undefined;
	const gateway = response?.extensions?.gateway;
	const requestId = typeof gateway?.request_id === 'string' ? gateway.request_id : undefined;
	const rawTraceId = gateway?.trace_id ?? gateway?.tId;
	const traceId = typeof rawTraceId === 'string' ? rawTraceId : undefined;
	recordLatestErrors(errors ?? null, { operationName: request.name, requestId, traceId });
}

/**
 * @deprecated Use getHeaders instead.
 */
function setOptions({
	endpoint,
	options,
}: {
	endpoint: string;
	options: RequestInit;
	request: RequestParameters;
	variables: Variables;
}): RequestInit {
	return generateOptions(options, endpoint);
}

function getHeaders({
	endpoint,
	options,
}: {
	endpoint: string;
	options: RequestInit;
	request: RequestParameters;
	variables: Variables;
}): HeadersInit {
	const generatedOptions = generateOptions(options, endpoint);
	return generatedOptions.headers || {};
}

function didPreflightAuthenticationCheckTriggerRedirect(
	_request: RequestParameters,
	_variables: Variables,
	_cacheConfig: CacheConfig,
): boolean {
	// `checkAuthentication` returns true when Jira is already redirecting or when it detects an
	// unauthenticated user and starts the login redirect. Relay-fetch uses that true result to skip
	// the network request and leave the observable pending while navigation takes over.
	return checkAuthentication();
}

const earlyScriptPromise = (
	request: RequestParameters,
	variables: Variables,
): Promise<Response> | undefined =>
	(!__SERVER__ && claimEarlyScriptPromise(getEarlyScriptPromiseKey(request, variables))) ||
	undefined;

const handleAbort = (request: RequestParameters, reason: string): void => {
	// We didn't receive everything!
	const err = new HttpError(424, `Incomplete handover for relay fetch: ${reason}`);
	err.name = 'ServerToClientError';
	getLoggingAndMetricsHelper({
		queryName: request.name,
		operationKind: request.operationKind,
	}).logError(err);
};

let abortSignal: AbortSignal | null = null;
/**
 * Sets the AbortSignal to use for all future fetches. This exists primarily for SSR to be able to abort fetching after a timeout.
 */
export const setAbortSignal = (newAbortSignal: AbortSignal | null): void => {
	abortSignal = newAbortSignal;
};
autoBeforeEachTest(() => {
	abortSignal = null;
});

export const getNetworkOptions = (): {
	withErrorHandling: typeof withErrorHandling;
	getLoggingAndMetricsHelper: (graphqlDetails: { queryName: string; operationKind: string }) => {
		logNonBreakingErrors: (errors?: PayloadError[] | null, requestId?: string) => void;
		logError: (error?: Error, retryAttempt?: number) => void;
		logErrorRetry: (error?: Error, retryAttempt?: number) => void;
		logQueryFallbackToPost: (reason: string) => void;
	};
	setEndpoint: typeof getReroutableURL;
	didPreflightAuthenticationCheckTriggerRedirect: typeof didPreflightAuthenticationCheckTriggerRedirect;
	validateResponse: typeof validateResponse;
	captureResponseErrors: typeof captureResponseErrors;
	onInvalidResponse: typeof onInvalidResponse;
	// @deprecated Use getHeaders instead
	setOptions: typeof setOptions;
	getHeaders: typeof getHeaders;
	earlyScriptPromise: (
		request: RequestParameters,
		variables: Variables,
	) => Promise<Response> | undefined;
	handleAbort: (request: RequestParameters, reason: string) => void;
	queryResponseCache: SSRQueryResponseCache;
	getAbortSignal: () => AbortSignal | null;
	isServer: boolean;
} => ({
	withErrorHandling,
	getLoggingAndMetricsHelper,
	setEndpoint: getReroutableURL,
	didPreflightAuthenticationCheckTriggerRedirect,
	validateResponse,
	captureResponseErrors,
	onInvalidResponse,
	// @deprecated Use getHeaders instead
	setOptions,
	getHeaders,
	earlyScriptPromise,
	handleAbort,
	queryResponseCache: getQueryResponseCache(),
	getAbortSignal: (): AbortSignal | null => abortSignal,
	isServer: __SERVER__,
});
